import { Component, forwardRef, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { QuillModule } from 'ngx-quill';

/**
 * Wrapper de ngx-quill con la toolbar estándar del proyecto.
 * Implementa ControlValueAccessor: úsalo con formControlName / ngModel.
 * El valor es HTML (texto enriquecido "ET" que se guarda tal cual en el back).
 */
@Component({
  selector: 'app-quill-editor',
  standalone: true,
  imports: [CommonModule, FormsModule, QuillModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => QuillEditorComponent),
      multi: true
    }
  ],
  template: `
    <quill-editor
      class="app-quill-editor"
      [ngModel]="value()"
      (ngModelChange)="onModelChange($event)"
      [modules]="modules"
      [placeholder]="placeholder()"
      [readOnly]="disabled()"
      (onBlur)="onTouched()"
      format="html"
      theme="snow">
    </quill-editor>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }
    .app-quill-editor {
      display: block;
      width: 100%;
    }
    :host ::ng-deep .ql-toolbar {
      border-radius: 4px 4px 0 0;
      border-color: var(--neutral-300, #ccc);
      font-family: 'Montserrat', sans-serif;
    }
    :host ::ng-deep .ql-container {
      border-radius: 0 0 4px 4px;
      border-color: var(--neutral-300, #ccc);
      font-family: 'Montserrat', sans-serif;
      font-size: 0.9rem;
    }
    :host ::ng-deep .ql-editor {
      min-height: 120px;
    }
  `]
})
export class QuillEditorComponent implements ControlValueAccessor {
  placeholder = input<string>('');

  readonly value = signal<string>('');
  readonly disabled = signal<boolean>(false);

  readonly modules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ header: [1, 2, 3, false] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['blockquote', 'code-block'],
      ['link'],
      ['clean']
    ]
  };

  private onChange: (value: string | null) => void = () => {};
  onTouched: () => void = () => {};

  onModelChange(html: string | null): void {
    this.value.set(html ?? '');
    this.onChange(html);
  }

  writeValue(value: string | null): void {
    this.value.set(value ?? '');
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }
}
